import 'styles/AppStyle.scss';
import { AppRoutes } from "./routes";
import 'i18n/config';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SignalRService from 'services/SignalRService';
import { RootState } from './store';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { NotificationPopup } from "components/notification";
import dayjs from "shared/dayjs";
import { useTranslation } from "react-i18next";
import { AppAdapterDayjs } from "shared/AppAdapterDayjs";
import { ThemeProvider } from 'services/ThemeContext';
import { useUnsavedChangesPrompt } from "hooks/useUnsavedChangesPrompt";
import TimeTrackingHub from 'services/TimeTrackingHub';
import WhatsNewModal from 'features/PatchNotes/components/WhatsNewModal';
import WhatsNewReopenButton from 'features/PatchNotes/components/WhatsNewReopenButton';
import TermsGateModal from 'features/Core/Terms/components/TermsGateModal';


function App() {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const auth = useSelector((state: RootState) => state.authentificationSlice);
  const ttSliceData = useSelector((state: RootState) => state.timeTrackingSlice);
  const signalRServiceRef = useRef<SignalRService | null>(null);
  const ttSignalRRef = useRef<TimeTrackingHub | null>(null);
  const ttInitializedRef = useRef(false);

  useEffect(() => {
    if (auth.hasLoginData) {
      signalRServiceRef.current = new SignalRService(dispatch);

      if (signalRServiceRef.current !== null) {
        signalRServiceRef.current.startConnection();
      }

      return () => {
        signalRServiceRef.current?.stopConnection();
      };
    }
  }, [auth.hasLoginData, dispatch]);

  useEffect(() => {
    ttSignalRRef.current = new TimeTrackingHub(dispatch);
    ttInitializedRef.current = true;

    if (ttSignalRRef.current !== null) {
      ttSignalRRef.current.startConnection();
    }

    return () => {
      ttSignalRRef.current?.stopConnection();
    };
  }, [ttSliceData.hasSessionState, dispatch]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" && (event.target as HTMLElement).tagName === "INPUT") {
        const formElement = (event.target as HTMLElement).closest("form");
        if (formElement) {
          event.preventDefault();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    dayjs.locale(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      document.querySelectorAll('.Mui-TableHeadCell-Content-Wrapper[title]').forEach(el => {
        el.removeAttribute('title');
      });
    });
    observer.observe(document.body, { childList: true, subtree: true, attributeFilter: ['title'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const start = () => document.body.classList.add('is-dragging');
    const end = () => document.body.classList.remove('is-dragging');
    document.addEventListener('dragstart', start);
    document.addEventListener('dragend', end);
    document.addEventListener('drop', end);
    return () => {
      document.removeEventListener('dragstart', start);
      document.removeEventListener('dragend', end);
      document.removeEventListener('drop', end);
    };
  }, []);

  useUnsavedChangesPrompt()

  return (
    <LocalizationProvider dateAdapter={AppAdapterDayjs} adapterLocale={i18n.language}>
      <ThemeProvider userTheme={auth.theme}>
        <div className="app">
          <AppRoutes />
          <NotificationPopup />
          {/* App-shell level, not inside a route: these surface on whatever page the user
              lands on after login. Terms is rendered last so it stacks above What's New;
              WhatsNewModal also refuses to auto-open while acceptance is outstanding, so the
              blocking gate is never competing with a dismissible modal. */}
          <WhatsNewModal />
          <WhatsNewReopenButton />
          <TermsGateModal />
        </div>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default App;